import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import Typography from "@mui/material/Typography";
import { PV } from "./pivotTokens";
import { formatRollupEta, PivotNoticeSeverity } from "./pivotDataSource";
import { PivotRollupWatch } from "./usePivotRollupWatch";

/**
 * Says that this view was read live rather than from the precomputed daily rollup — the
 * slow path — and, when a rebuild is running, how far along it is.
 *
 * It is not an error state: the numbers under it are correct and current. The progress bar
 * is the whole reason to show anything at all, since it is what turns "this page is broken"
 * into "this page is catching up", and it disappears on its own when the rebuild ends and
 * the table refetches.
 */
export default function PivotDataSourceNotice({
  message,
  severity,
  watch,
  title,
}: {
  message: string;
  severity: PivotNoticeSeverity;
  /** `null` when nothing is rebuilding — then this is a plain one-line notice. */
  watch: PivotRollupWatch | null;
  /** Set on the CSV notice, which otherwise reads as a second opinion about the table. */
  title?: string;
}) {
  const eta = formatRollupEta(watch?.etaSeconds);
  const percent = watch?.percent;

  return (
    <Alert severity={severity} sx={{ mb: 2, borderRadius: PV.radius }}>
      {title ? `${title} — ${message}` : message}

      {watch && (
        <Box sx={{ mt: 1 }}>
          <LinearProgress
            // Progress is null until the first poll answers, and stays null on a poll that
            // could not measure it — an indeterminate bar is honest about that, a 0% one is
            // not.
            variant={percent === null || percent === undefined ? "indeterminate" : "determinate"}
            value={percent ?? 0}
            sx={{ height: 6, borderRadius: 3 }}
          />
          <Typography sx={{ fontSize: 12.5, color: PV.tx3, mt: 0.5 }}>
            {percent === null || percent === undefined
              ? "กำลังปรับปรุงข้อมูลสรุปอยู่เบื้องหลัง"
              : `กำลังปรับปรุงข้อมูลสรุปอยู่เบื้องหลัง ${percent}%`}
            {eta ? ` · เหลืออีก${eta}` : ""}
            {" · หน้านี้จะโหลดตัวเลขใหม่ให้เองเมื่อเสร็จ"}
          </Typography>
        </Box>
      )}
    </Alert>
  );
}
